import * as React from "react";
import classNames from "classnames";
import {
	WithStyles,
	Theme,
	createStyles,
	withStyles,
	ListItemSecondaryAction
} from "@material-ui/core";
import { connect } from "react-redux";

import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/Button";
import Paper from "@material-ui/core/Paper";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import TextField from "@material-ui/core/TextField";
import AlternateEmailIcon from "@material-ui/icons/AlternateEmail";
import GetAppIcon from "@material-ui/icons/GetApp";

import { Dispatch } from "redux";
import { StoreState } from "../../reducers/root-reducer";
import { Table } from "../../api/tables";
import { ConnectedSheet } from "../sheet";
import { formatDate, format } from "../../util/date-utility";
import { SnackbarVariant } from "../../actions/store/type";
import { setSnackbarMessage } from "../../actions/store/snackbar-actions";
import {
	requestOccupancyPlanEmail,
	requestOccupancyListEmail,
	requestOccupancyListExcelEmail,
	requestOccupancyListBlob,
	requestOccupancyListExcelBlob,
	requestOccupancyPlanBlob,
	requestPreorderedMealsListBlob
} from "../../api/files";
import { downloadWithFilename } from "../../util/general-utility";
import { ConnectedPrintviewListItem } from "./list-item";
import { Area } from "../../api/areas";

interface State {
	showSendEmailDialog: boolean;
	dialogEmail: string;
	dialogTarget: "plan" | "list" | "list-xlsx" | "preordered-meals";
}

export interface PrintviewListProps extends Table {}

interface ConnectedPrintviewListProps extends WithStyles<typeof styles> {
	areas: Area[];
	selectedDate: Date;
	selectedTime: string;
	setSnackbarMessage: (message: string, variant?: SnackbarVariant) => void;
}

type Props = PrintviewListProps & ConnectedPrintviewListProps;

export class PrintviewList extends React.Component<Props, State> {
	public state: State = {
		showSendEmailDialog: false,
		dialogEmail: "",
		dialogTarget: null
	};

	private handleDialogEmailChange = (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		this.setState({ dialogEmail: event.currentTarget.value });
	};

	private handleDialogCancelClick = () => {
		this.setState({
			showSendEmailDialog: false
		});
	};

	private handleDialogSubmitClick = async () => {
		const { selectedDate, selectedTime, setSnackbarMessage } = this.props;
		const { dialogEmail, dialogTarget } = this.state;

		if (dialogEmail === "") {
			setSnackbarMessage(
				"E-Mail Adresse darf nicht leer sein!",
				"warning"
			);

			return;
		}

		try {
			localStorage.setItem("printDialogEmail", dialogEmail.toLowerCase());

			if (dialogTarget === "plan") {
				await requestOccupancyPlanEmail(selectedDate, dialogEmail, selectedTime);
			} else if (dialogTarget === "list") {
				await requestOccupancyListEmail(selectedDate, dialogEmail, selectedTime);
			} else if (dialogTarget === "list-xlsx") {
				await requestOccupancyListExcelEmail(selectedDate, dialogEmail, selectedTime);
			}

			setSnackbarMessage("E-Mail verschickt!", "success");
		} catch (e) {
			if (
				e
					.toString()
					.toLowerCase()
					.indexOf("not found") > -1
			) {
				setSnackbarMessage(
					"Keine Reservierungen für den gewählten Tag verfügbar!",
					"warning"
				);
			} else {
				setSnackbarMessage("Konnte E-Mail nicht senden!", "error");
			}
		}

		this.handleDialogCancelClick();
	};

	private handleSendOccupancyPlanEmailClick = () => {
		this.setState({
			showSendEmailDialog: true,
			dialogEmail: localStorage.getItem("printDialogEmail") || "",
			dialogTarget: "plan"
		});
	};

	private handleSendOccupancyListEmailClick = () => {
		this.setState({
			showSendEmailDialog: true,
			dialogEmail: localStorage.getItem("printDialogEmail") || "",
			dialogTarget: "list"
		});
	};

	private handleSendOccupancyListEmailExcelClick = () => {
		this.setState({
			showSendEmailDialog: true,
			dialogEmail: localStorage.getItem("printDialogEmail") || "",
			dialogTarget: "list-xlsx"
		});
	};

	private handleSendPreorderedMealsListEmailClick = () => {
		this.setState({
			showSendEmailDialog: true,
			dialogEmail: localStorage.getItem("printDialogEmail") || "",
			dialogTarget: "preordered-meals"
		});
	};

	private handleDownloadOccupancyPlanClick = (area: Area) => {
		this.downloadFile(requestOccupancyPlanBlob, "belegungsplan", area, "pdf");
	};

	private handleDownloadOccupancyListClick = (area: Area) => {
		this.downloadFile(requestOccupancyListBlob, "belegungsliste", area, "pdf");
	};

	private handleDownloadOccupancyListExcelClick = (area: Area) => {
		this.downloadFile(requestOccupancyListExcelBlob, "belegungsliste", area, "xlsx");
	};

	private handleDownloadPreorderedMealsListClick = (area: Area) => {
		this.downloadFile(requestPreorderedMealsListBlob, "vorbestellte-speisen", area, "pdf");
	};

	private getDialogContent = (dialogTarget: "plan" | "list" |  "list-xlsx" | "preordered-meals") => {
		switch (dialogTarget) {
			case "plan":
				return "Belegungsplan";

			case "list":
				return "Belegungsliste als PDF";

			case "list-xlsx":
				return "Belegungsliste als Excel";

			case "preordered-meals":
				return "Vorbestellte Speisen";
		}
	};

	private downloadFile = async (
		requestFunc: Function,
		namePrefix: string,
		area: Area,
		fileExtension: string
	) => {
		const { selectedDate, selectedTime, areas, setSnackbarMessage } = this.props;

		const downloadArea = async (area: Area, showNotFound = true) => {
			try {
				const res = (await requestFunc(selectedDate, area.id, selectedTime))
					.data as Response;

				const file = await res.blob();

				const url = URL.createObjectURL(file);
				const filename =
					namePrefix +
					"-" +
					area.name.toLowerCase() +
					"-" +
					format(selectedDate, "DD-MM-YYYY") +
					"." +
					fileExtension;

				downloadWithFilename(url, filename);
				URL.revokeObjectURL(url);
			} catch (e) {
				if (e.message && e.message === "Not Found") {
					if (showNotFound) {
						setSnackbarMessage(
							"Keine Reservierungen für " +
								area.name +
								" vorhanden!",
							"warning"
						);
					}
				} else {
					console.error(e);

					setSnackbarMessage(
						"Datei konnte nicht erstellt werden!",
						"error"
					);
				}
			}
		};

		if (area) {
			downloadArea(area);
		} else {
			let timeout = 0;
			areas.forEach(area => {
				setTimeout(() => downloadArea(area, false), timeout);
				timeout += 0;
			});
		}
	};

	public render() {
		const { classes, selectedDate } = this.props;
		const { showSendEmailDialog, dialogEmail, dialogTarget } = this.state;

		return (
			<ConnectedSheet maxWidth={1200}>
				<Typography className={classes.sectionTitle}>
					Druckansichten für {formatDate(selectedDate)}
				</Typography>
				<Paper>
					<List>
						<ConnectedPrintviewListItem
							title="Belegungsplan"
							onEMailClick={
								this.handleSendOccupancyPlanEmailClick
							}
							onDownloadClick={
								this.handleDownloadOccupancyPlanClick
							}
							areaSelect={true}
							allowMultiDownloads={true}
						/>
						<ConnectedPrintviewListItem
							title="Belegungsliste als PDF"
							onEMailClick={
								this.handleSendOccupancyListEmailClick
							}
							onDownloadClick={
								this.handleDownloadOccupancyListClick
							}
							areaSelect={true}
							allowMultiDownloads={true}
						/>
						<ConnectedPrintviewListItem
							title="Belegungsliste als Excel"
							onEMailClick={
								this.handleSendOccupancyListEmailExcelClick
							}
							onDownloadClick={
								this.handleDownloadOccupancyListExcelClick
							}
							areaSelect={true}
							allowMultiDownloads={false}
						/>
						{/*
						<ConnectedPrintviewListItem
							title="Vorbestellte Speisen"
							onEMailClick={
								this.handleSendPreorderedMealsListEmailClick
							}
							onDownloadClick={
								this.handleDownloadPreorderedMealsListClick
							}
							allowMultiDownloads={false}
						/>
					    */}
					</List>
				</Paper>
				<Dialog
					open={showSendEmailDialog}
					onClose={this.handleDialogCancelClick}
				>
					<DialogTitle>Per E-Mail senden</DialogTitle>
					<DialogContent>
						<DialogContentText>
							{this.getDialogContent(dialogTarget)}&nbsp;an die folgende E-Mail Adresse senden:
						</DialogContentText>
						<TextField
							autoFocus
							margin="dense"
							type="text"
							fullWidth
							value={dialogEmail}
							label="E-Mail Adresse"
							onChange={this.handleDialogEmailChange}
						/>
					</DialogContent>
					<DialogActions>
						<Button onClick={this.handleDialogCancelClick}>
							Abbrechen
						</Button>
						<Button
							onClick={this.handleDialogSubmitClick}
							color="primary"
						>
							Senden
						</Button>
					</DialogActions>
				</Dialog>
			</ConnectedSheet>
		);
	}
}

const mapStateToProps = (state: StoreState) => ({
	areas: state.areas,
	selectedTime: state.overviewActiveTimeId,
	selectedDate: state.date
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
	setSnackbarMessage: (message: string, variant: SnackbarVariant) =>
		dispatch(setSnackbarMessage(message, variant))
});

const styles = (theme: Theme) =>
	createStyles({
		sectionTitle: {
			fontSize: "1.2rem",
			fontWeight: 500,
			marginBottom: 10
		},
		button: {
			marginRight: 5
		}
	});

export const ConnectedPrintviewList = withStyles(styles)(
	connect(
		mapStateToProps,
		mapDispatchToProps
	)(PrintviewList)
);
